/**
 * General-purpose app icon set, in the same hand-drawn line style as the
 * sermon editor's toolbar icons (components/editor-icons.tsx): a 24×24
 * viewBox, no fill, `currentColor` stroke at width 1.7, round caps and
 * joins. Render at 18px by default; pass `size`/`className` to adjust.
 *
 * These back the icon-only actions in IconButton (Remove, Revoke, Email
 * link, Leave, Rotate) so affordances read as glyphs instead of tiny
 * underlined text links. Kept separate from editor-icons.tsx, which is
 * scoped to the TipTap toolbar.
 */
import type { ReactNode } from 'react';

export interface IconProps {
  /** Pixel size of the square glyph. Default 18. */
  size?: number;
  className?: string;
}

function Icon({ size = 18, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {children}
    </svg>
  );
}

/** Trash can — remove a member, delete an item. Path lifted from the
 *  inline icon previously in routes/photo.tsx so the glyph stays uniform. */
export function IconTrash(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 7h14M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M7 7l1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" />
    </Icon>
  );
}

/** Envelope — send an email sign-in link. */
export function IconMail(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 6h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z" />
      <path d="m4 7 8 6 8-6" />
    </Icon>
  );
}

/** Circle with an X — revoke a pending invitation. */
export function IconXCircle(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m9 9 6 6M15 9l-6 6" />
    </Icon>
  );
}

/** Doorway with an out-arrow — leave a circle / reading plan. */
export function IconExit(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3" />
      <path d="m14 8 4 4-4 4" />
      <path d="M18 12H9" />
    </Icon>
  );
}

/** Circular arrow — rotate / regenerate the invite code. */
export function IconRefresh(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      <path d="M20 4v4h-4" />
    </Icon>
  );
}

/** Plus — the "add" affordance (e.g. the Memories add menu trigger). */
export function IconPlus(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

/** Framed picture — "Add photo". Same glyph as the editor toolbar's
 *  image icon so the two read as one family. */
export function IconImage(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="9" cy="9" r="1.6" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.83 0L6 21" />
    </Icon>
  );
}

/** Bookmark/ribbon — "New memory" (a moment set aside to remember). */
export function IconBookmark(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 4h12a1 1 0 0 1 1 1v15l-7-4-7 4V5a1 1 0 0 1 1-1z" />
    </Icon>
  );
}

/** Pencil — edit an item (memory, sermon note, caption). */
export function IconPencil(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20h4l10-10a2 2 0 0 0-2.83-2.83L5.17 17.17 4 20z" />
      <path d="m13.5 6.5 4 4" />
    </Icon>
  );
}

/** Check — confirm/save an inline edit, mark notifications read. */
export function IconCheck(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m5 13 4 4L19 7" />
    </Icon>
  );
}

/** Left arrow — back / return to the previous screen. */
export function IconArrowLeft(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M19 12H5" />
      <path d="m11 6-6 6 6 6" />
    </Icon>
  );
}

/** Heart — "like" a photo. Filled when active. */
export function IconHeart({
  filled = false,
  ...props
}: IconProps & { filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={props.size ?? 18}
      height={props.size ?? 18}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
      className={props.className}
      aria-hidden
    >
      <path d="M12 21s-7-4.35-9-9.27C1.5 8.5 3.5 5 7 5c2 0 3.5 1.16 5 3 1.5-1.84 3-3 5-3 3.5 0 5.5 3.5 4 6.73C19 16.65 12 21 12 21z" />
    </svg>
  );
}

/** Speech bubble — comment count. */
export function IconComment(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 5h16v11H8.5L4 19V5z" />
    </Icon>
  );
}
